import React from 'react';
import { createPortal } from 'react-dom';

/**
 * Reusable explanation tooltip. Generalised from the care-team `.ct-info`
 * pattern, with three upgrades needed for the biomarker rows:
 *   - portal + fixed positioning, so it escapes the card's `overflow:hidden`
 *     and never clips inside a scroll container;
 *   - viewport clamping + auto flip (above/below), so tips near a screen edge
 *     stay on-screen;
 *   - tap-to-toggle, so it works on touch devices where hover doesn't exist.
 *
 * Usage:
 *   <InfoTip content="ApoB counts the particles…">ApoB</InfoTip>  // wraps a trigger
 *   <InfoTip content="…" />                                       // standalone "i" badge
 */

interface InfoTipProps {
  content: React.ReactNode;
  /** Trigger content. When omitted, a small circular "i" badge is rendered. */
  children?: React.ReactNode;
  /** Accessible label for the trigger (defaults to a generic hint). */
  ariaLabel?: string;
  /** Append an "i" badge after the trigger children. */
  showIcon?: boolean;
}

interface Coords {
  left: number;
  top: number;
  placement: 'above' | 'below';
}

const BUBBLE_MAX_WIDTH = 268;
const GAP = 9;

export default function InfoTip({ content, children, ariaLabel, showIcon = true }: InfoTipProps) {
  const [open, setOpen] = React.useState(false);
  const [coords, setCoords] = React.useState<Coords | null>(null);
  const triggerRef = React.useRef<HTMLSpanElement | null>(null);

  const place = React.useCallback(() => {
    const el = triggerRef.current;
    if (!el || typeof window === 'undefined') return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const centre = rect.left + rect.width / 2;
    const left = Math.min(Math.max(centre, 8 + BUBBLE_MAX_WIDTH / 2), vw - 8 - BUBBLE_MAX_WIDTH / 2);
    const spaceAbove = rect.top;
    const placement: Coords['placement'] = spaceAbove > 120 ? 'above' : 'below';
    const top = placement === 'above' ? rect.top - GAP : rect.bottom + GAP;
    setCoords({ left, top, placement });
  }, []);

  const show = React.useCallback(() => {
    place();
    setOpen(true);
  }, [place]);
  const hide = React.useCallback(() => setOpen(false), []);

  React.useEffect(() => {
    if (!open) return undefined;
    const onScrollOrResize = () => place();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hide();
    };
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, place, hide]);

  return (
    <span
      ref={triggerRef}
      className="melio-tip-trigger"
      role="note"
      tabIndex={0}
      aria-label={ariaLabel}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onClick={(e) => {
        e.stopPropagation();
        open ? hide() : show();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open ? hide() : show();
        }
      }}
    >
      {children}
      {showIcon && <span className="melio-tip-badge" aria-hidden>i</span>}
      {open && coords && typeof document !== 'undefined'
        ? createPortal(
            <span
              className="melio-tip-bubble"
              style={{
                position: 'fixed',
                left: coords.left,
                top: coords.top,
                transform: `translate(-50%, ${coords.placement === 'above' ? '-100%' : '0'})`,
                maxWidth: BUBBLE_MAX_WIDTH,
              }}
            >
              {content}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}
